/**
 * SlaBreachBadge — compact navbar badge that surfaces SLA breaches
 * on EVERY admin page (Zorzutti CR 03/08/2026).
 *
 * Design goal:
 *   Fabrizio should see the red flag the moment he lands on any
 *   /admin/* route, without having to scroll to the dashboard SLA
 *   widget. Peripheral vision > cognition.
 *
 * Behaviour:
 *   • Hidden when no request is over the alert threshold — zero
 *     noise on the good-path (green state doesn't earn nav space).
 *   • Amber "attention" pill when ≥1 request is between 20h and 24h.
 *   • Red pulsing pill with count when ≥1 request has BREACHED 24h.
 *   • Click → navigates to /admin#sla-health so the user lands right
 *     on the detailed widget.
 *   • Polls the same ``/api/admin/system/change-requests/sla-status``
 *     endpoint every 60s. Silent-fail on network/auth errors: the
 *     navbar must never crash because of this badge.
 *
 * Auth model: uses the existing ``lojack_token`` from localStorage
 * (same source as ``useAuth`` context). We attach it via a native
 * ``fetch`` Authorization header so the badge is decoupled from the
 * global axios interceptor stack — one fewer moving part.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, TimerReset } from "lucide-react";
import { API } from "../../config";

const POLL_INTERVAL_MS = 60 * 1000;   // 60s — matches the dashboard widget
const INITIAL_DELAY_MS = 900;         // small offset from other startup fetches

export const SlaBreachBadge = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = typeof window !== "undefined"
        ? localStorage.getItem("lojack_token")
        : null;
      if (!token) {
        setHidden(true);
        return;
      }
      const r = await fetch(`${API}/admin/system/change-requests/sla-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        // 401/403 → user isn't allowed; just hide silently.
        if (r.status === 401 || r.status === 403) setHidden(true);
        return;
      }
      const payload = await r.json();
      setData(payload);
      setHidden(false);
    } catch (_) {
      // Network failure — keep last known state, do NOT hide the
      // badge (a transient error shouldn't erase a real breach).
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchStatus, INITIAL_DELAY_MS);
    const iv = setInterval(fetchStatus, POLL_INTERVAL_MS);
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimeout(t);
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchStatus]);

  if (hidden || !data) return null;

  const breach = data.breach_count || 0;
  const overAlert = data.over_alert_threshold || 0;

  // Show ONLY when there's something operationally interesting.
  // Green state (all <20h) earns zero nav real estate.
  if (breach === 0 && overAlert === 0) return null;

  const level = breach > 0 ? "breach" : "alert";
  const Icon = breach > 0 ? AlertTriangle : TimerReset;
  const count = breach > 0 ? breach : overAlert;
  const style = level === "breach"
    ? {
        color: "#fecaca",
        bg: "rgba(220,38,38,0.18)",
        border: "rgba(239,68,68,0.55)",
        pulse: true,
      }
    : {
        color: "#fde68a",
        bg: "rgba(217,119,6,0.15)",
        border: "rgba(245,158,11,0.50)",
        pulse: false,
      };

  const label = level === "breach"
    ? (count === 1 ? "SLA sforata" : "SLA sforate")
    : "SLA a rischio";
  const tooltip = level === "breach"
    ? `${count} richiest${count === 1 ? "a" : "e"} di correzione ha${count === 1 ? "" : "nno"} sforato le ${data.thresholds?.breach_hours || 24}h. Clicca per intervenire.`
    : `${count} richiest${count === 1 ? "a" : "e"} sopra le ${data.thresholds?.alert_hours || 20}h (soglia allerta). Clicca per intervenire.`;

  return (
    <button
      type="button"
      onClick={() => navigate("/admin#sla-health")}
      title={tooltip}
      aria-label={tooltip}
      data-testid="sla-breach-badge"
      data-sla-level={level}
      data-sla-count={count}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-all border cursor-pointer hover:brightness-110 ${
        style.pulse ? "animate-pulse" : ""
      }`}
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
      }}
    >
      <Icon size={12} strokeWidth={2.5} />
      <span className="tabular-nums">{count}</span>
      <span>{label}</span>
    </button>
  );
};

export default SlaBreachBadge;
